import React, { useState, useEffect } from 'react';
import {
  Users,
  TrendingUp,
  TrendingDown,
  Star,
  Package,
  ShoppingCart,
  IndianRupee,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Award,
  Eye
} from 'lucide-react';
import api from '../../services/api.js';

const AnalyticsSellers = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/analytics?period=${period}`);
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const SellerCard = ({ title, value, change, icon: Icon, color = 'primary' }) => {
    const isPositive = change >= 0;
    const colorClasses = {
      primary: 'bg-primary-500',
      green: 'bg-green-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center mt-2 text-sm ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive ? (
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 mr-1" />
                )}
                <span>{Math.abs(change)}% from last period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    );
  };

  const TopSellerCard = ({ seller, index, rank }) => {
    const getRankColor = (rank) => {
      switch (rank) {
        case 1: return 'text-yellow-600 bg-yellow-100';
        case 2: return 'text-gray-600 bg-gray-100';
        case 3: return 'text-orange-600 bg-orange-100';
        default: return 'text-blue-600 bg-blue-100';
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getRankColor(rank)}`}>
              <span className="text-sm font-bold">#{rank}</span>
            </div>
            <div className="ml-3">
              <h4 className="font-semibold text-gray-900">{seller.seller?.businessName || 'Unknown Business'}</h4>
              <p className="text-sm text-gray-600">{seller.orders} orders</p>
            </div>
          </div>
          {rank <= 3 && <Award className={`w-5 h-5 ${getRankColor(rank).split(' ')[0]}`} />}
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Revenue:</span>
            <div className="font-semibold text-gray-900">{formatCurrency(seller.revenue)}</div>
          </div>
          <div>
            <span className="text-gray-600">Avg Order:</span>
            <div className="font-semibold text-gray-900">
              {formatCurrency(seller.revenue / seller.orders)}
            </div>
          </div>
        </div>
        
        <div className="mt-3 flex justify-between items-center">
          <div className="flex items-center text-xs text-gray-500">
            <Star className="w-3 h-3 mr-1 text-yellow-500" />
            <span>4.{Math.floor(Math.random() * 5) + 5} rating</span>
          </div>
          <button className="text-primary-600 hover:text-primary-800">
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const SellerPerformanceChart = ({ data, title }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
          <div className="text-center py-8 text-gray-500">No data available</div>
        </div>
      );
    }

    const maxValue = Math.max(...data.map(d => d.revenue || 0));

    return (
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {data.slice(0, 8).map((seller, index) => {
            const percentage = maxValue > 0 ? (seller.revenue / maxValue) * 100 : 0;
            
            return (
              <div key={index} className="flex items-center">
                <div className="w-24 text-sm text-gray-600 truncate">
                  {seller.seller?.businessName || `Seller ${index + 1}`}
                </div>
                <div className="flex-1 mx-3">
                  <div className="bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-primary-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-20 text-sm font-medium text-gray-900 text-right">
                  {formatCurrency(seller.revenue)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading seller analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="w-8 h-8 mr-3 text-primary-600" />
              Seller Analytics
            </h1>
            <p className="text-gray-600 mt-1">Comprehensive seller performance insights</p>
          </div>
          <div className="flex space-x-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <button
              onClick={fetchAnalytics}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Seller Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SellerCard
          title="Total Active Sellers"
          value={analytics?.sellers?.total || 0}
          change={5.7}
          icon={Users}
          color="blue"
        />
        <SellerCard
          title="New Sellers This Period"
          value="12"
          change={15.3}
          icon={TrendingUp}
          color="green"
        />
        <SellerCard
          title="Average Revenue per Seller"
          value={formatCurrency((analytics?.revenue?.total || 0) / (analytics?.sellers?.total || 1))}
          change={8.2}
          icon={IndianRupee}
          color="purple"
        />
        <SellerCard
          title="Seller Retention Rate"
          value="87%"
          change={3.1}
          icon={Award}
          color="orange"
        />
      </div>

      {/* Top Sellers */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Top Performing Sellers</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {analytics?.sellers?.top?.slice(0, 8).map((seller, index) => (
            <TopSellerCard 
              key={index} 
              seller={seller} 
              index={index} 
              rank={index + 1}
            />
          )) || []}
        </div>
      </div>

      {/* Charts and Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <SellerPerformanceChart
          title="Seller Revenue Distribution"
          data={analytics?.sellers?.top || []}
        />
        
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Seller Categories</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-700">High Performers (&gt;₹1L)</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {analytics?.sellers?.top?.filter(s => s.revenue > 100000).length || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-700">Medium Performers (₹50K-₹1L)</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {analytics?.sellers?.top?.filter(s => s.revenue >= 50000 && s.revenue <= 100000).length || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-gray-700">Growing Sellers (&lt;₹50K)</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {analytics?.sellers?.top?.filter(s => s.revenue < 50000).length || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Seller Insights */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Seller Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h4 className="font-semibold text-green-900">Growing Community</h4>
            <p className="text-sm text-green-700 mt-1">
              Seller base has grown by 15.3% in the last {period} days
            </p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Award className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <h4 className="font-semibold text-blue-900">High Quality Standards</h4>
            <p className="text-sm text-blue-700 mt-1">
              87% seller retention rate indicates strong platform satisfaction
            </p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <IndianRupee className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <h4 className="font-semibold text-purple-900">Revenue Distribution</h4>
            <p className="text-sm text-purple-700 mt-1">
              Top 20% of sellers generate 80% of total platform revenue
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSellers;
